import React, {useCallback, useEffect, useState} from 'react';
import {MatrixEvent} from 'matrix-js-sdk';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import DialogWithCloseIcon from '@/components/ui/dialogs/dialog-with-close-icon';
import VideoThumbnails from './video-thumbnail';
import {useMatrixContext} from '@/contexts/matrix/client';
import {getUrlsFromEvents} from '@/components/matrix-chat/utils/helper-functions';
import BoxWithTwoElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-two-elements';
import BoxWithThreeElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-three-elements';
import BoxWithFourElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-four-elements';
import BoxWithFiveElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-five-elements';
import BoxWithMoreThanFiveElements from '@/components/matrix-chat/ui/multimedia-boxes/box-with-more-than-five-elements';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {useTranslation} from 'react-i18next';
import ContentDisplayBox from '@/components/matrix-chat/ui/content-display-box';
import {
  AdditionalMultimediaInfo,
  EventPosition,
  Multimedia,
  MultimediaDownloadInfo
} from '@/types/matrix';
import BoxWithOneElement from '@/components/matrix-chat/ui/multimedia-boxes/box-with-one-element';
import {Box} from '@mui/material';

interface MultiVideoBoxProps {
  videoEvent: MatrixEvent;
  eventPosition: EventPosition;
}

const MultiVideoBox: React.FC<MultiVideoBoxProps> = ({
  videoEvent,
  eventPosition
}) => {
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const [activeVideoInfo, setActiveVideoInfo] =
    useState<MultimediaDownloadInfo | null>(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [videoDeletionStarted, setVideoDeletionStarted] = useState(false);

  const {matrixUserId} = useMatrixContext();

  const {
    handleCloseMultimediaDialog,
    handleOpenMultimediaDialog,
    handleDeleteMatrixEvent,
    handleDownloadSingleMultimedia,
    handleDownloadMultipleMultimedia
    //handleDeleteVideo
  } = useMatrixRoomContext();

  const {t} = useTranslation();

  const effectiveEvent = videoEvent.getEffectiveEvent();

  const text = effectiveEvent.content.body;

  const isOwnMessage = effectiveEvent.sender === matrixUserId;

  const isSingleVideoMessage = videoUrls.length === 1;

  const eventMimeType = effectiveEvent.content.info.mimetype;

  const handleOpenDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(true);
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleGetMultimediaDownloadInfo = useCallback(
    (url: string) => {
      const urlSplit = url.split('/');
      const lastUrlElement = urlSplit[urlSplit.length - 1];
      const additionalInfo = effectiveEvent.content.additional.find(
        (item: AdditionalMultimediaInfo) => {
          const urlSplit = item.url.split('/');
          const urlSuffixFromEvent = urlSplit[urlSplit.length - 1];

          return urlSuffixFromEvent === lastUrlElement;
        }
      );
      return {
        type: 'image' as Multimedia,
        url,
        mimeType: additionalInfo ? additionalInfo.info.mimetype : eventMimeType
      };
    },
    [effectiveEvent, eventMimeType]
  );

  // TODO: We need to specify type of video messages!
  const handleDownloadInIconPanel = useCallback(() => {
    if (videoUrls.length === 1) {
      handleDownloadSingleMultimedia({
        url: videoUrls[0],
        type: 'video',
        mimeType: eventMimeType
      });
    } else {
      const multimediaDownloadInfo = videoUrls.map((url) => {
        return handleGetMultimediaDownloadInfo(url);
      });
      handleDownloadMultipleMultimedia(multimediaDownloadInfo);
    }
  }, [
    eventMimeType,
    handleDownloadMultipleMultimedia,
    handleDownloadSingleMultimedia,
    handleGetMultimediaDownloadInfo,
    videoUrls
  ]);

  const handleDeleteClick = useCallback(async () => {
    try {
      await handleDeleteMatrixEvent(videoEvent);

      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error('Failed to delete the message:', error);
    }
  }, [handleCloseDeleteConfirmation, handleDeleteMatrixEvent, videoEvent]);

  const handleOpenFullScreen = useCallback(
    (videoUrl: string) => {
      const videoInfo = handleGetMultimediaDownloadInfo(videoUrl);
      setActiveVideoInfo(videoInfo);

      handleOpenMultimediaDialog();
    },
    [handleGetMultimediaDownloadInfo, handleOpenMultimediaDialog]
  );

  const handleCloseFullScreen = useCallback(() => {
    setActiveVideoInfo(null);
    handleCloseMultimediaDialog();
  }, [handleCloseMultimediaDialog]);

  const onSingleVideoDeleted = useCallback(() => {
    setVideoDeletionStarted(true);
    handleCloseFullScreen();
  }, [handleCloseFullScreen]);

  const renderVideoLayout = useCallback(
    (videoCount: number) => {
      switch (videoCount) {
        case 1:
          return (
            <BoxWithOneElement
              urls={videoUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="video"
            />
          );
        case 2:
          return (
            <BoxWithTwoElements
              urls={videoUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="video"
            />
          );
        case 3:
          return (
            <BoxWithThreeElements
              urls={videoUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="video"
            />
          );

        case 4:
          return (
            <BoxWithFourElements
              urls={videoUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="video"
            />
          );
        case 5:
          return (
            <BoxWithFiveElements
              urls={videoUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="video"
            />
          );

        default:
          return (
            <BoxWithMoreThanFiveElements
              urls={videoUrls}
              handleOpenFullScreen={handleOpenFullScreen}
              multimediaType="video"
            />
          );
      }
    },
    [handleOpenFullScreen, videoUrls]
  );

  useEffect(() => {
    if (videoEvent || videoDeletionStarted) {
      const contentFromVideoEvent = videoEvent.getEffectiveEvent().content;

      if (
        contentFromVideoEvent &&
        'url' in contentFromVideoEvent &&
        'info' in contentFromVideoEvent &&
        'additional' in contentFromVideoEvent
      ) {
        const newContent = {
          url: contentFromVideoEvent.url,
          info: contentFromVideoEvent.info,
          additional: contentFromVideoEvent.additional,
          body: contentFromVideoEvent.body || ''
        };

        const urls = getUrlsFromEvents(newContent);
        setVideoUrls(urls);
        setVideoDeletionStarted(false);
      } else {
        setVideoUrls([]);
        setVideoDeletionStarted(false);
      }
    }
  }, [videoDeletionStarted, videoEvent]);

  return (
    <>
      <ContentDisplayBox
        event={videoEvent}
        eventPosition={eventPosition}
        messageText={text}
        onDownloadIconPanel={handleDownloadInIconPanel}
        onDeleteIconPanel={handleOpenDeleteConfirmation}
      >
        {renderVideoLayout(videoUrls.length)}
        {activeVideoInfo && videoEvent && (
          <DialogWithCloseIcon
            open={Boolean(activeVideoInfo)}
            onClose={handleCloseFullScreen}
            chatMode={true}
            onDownload={handleDownloadSingleMultimedia}
            //onDeleteEvent={handleDeleteVideo}
            matrixEvent={videoEvent}
            isOwnMessage={isOwnMessage}
            onDeleted={onSingleVideoDeleted}
            customDialogTitle={'delete_video_title'}
            customDialogContent={'delete_video_dialog_content'}
            multimediaInfo={activeVideoInfo}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              width="100%"
            >
              <video
                src={activeVideoInfo.url}
                controls
                muted
                autoPlay
                style={{
                  maxWidth: '100%',
                  maxHeight: isSingleVideoMessage ? '75vh' : '60vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
            {videoUrls && !isSingleVideoMessage && (
              <VideoThumbnails
                selectedVideo={activeVideoInfo.url}
                allUrls={videoUrls}
                onSelectVideo={(url: string) => {
                  const videoInfo = handleGetMultimediaDownloadInfo(url);
                  setActiveVideoInfo(videoInfo);
                }}
              />
            )}
          </DialogWithCloseIcon>
        )}

        <ConfirmationDialog
          open={deleteConfirmationOpen}
          title={t('delete_video_multiple_title')}
          message={t('delete_video_multiple_dialog_content')}
          onConfirm={handleDeleteClick}
          onCancel={handleCloseDeleteConfirmation}
        />
      </ContentDisplayBox>
    </>
  );
};

export default MultiVideoBox;
